import { PermissionService } from '@/app/core/services/permission.service';
import { ExtendedLandViewModel, PlantationService } from '@/app/core/services/plantation.service';
import { ActivityStatusViewModel, ActivityTypeViewModel, EnterpriseMemberViewModel, GroupedPlantationViewModel, KeyValueModel, UnitTypeLookupViewModel, UserEnterpriseInfoViewModel } from '@/app/proxy/proxies';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, filter, take } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ActivitiesService {
  private _plantationService = inject(PlantationService);

  enterpriseUsers: EnterpriseMemberViewModel[] = [];
  activityTypes: ActivityTypeViewModel[] = [];
  activityStatuses: ActivityStatusViewModel[] = [];

  unitsLookup: UnitTypeLookupViewModel[] = [];
  weatherStatusesLookup: KeyValueModel[] = [];

  readonly ENTERPRISE_STORAGE_KEY = 'activities_selected_enterprise_id';

  userEnterprises: UserEnterpriseInfoViewModel[] = [];
  private userEnterprisesSubject = new BehaviorSubject<UserEnterpriseInfoViewModel[]>([]);
  userEnterprises$ = this.userEnterprisesSubject.asObservable();
  setUserEnterprises(enterprises: UserEnterpriseInfoViewModel[]) {
    this.userEnterprisesSubject.next(enterprises);
  }

  selectedEnterprise: UserEnterpriseInfoViewModel;
  private selectedEnterpriseSubject = new BehaviorSubject<UserEnterpriseInfoViewModel>(null);
  selectedEnterprise$ = this.selectedEnterpriseSubject.asObservable();
  setSelectedEnterprise(enterprise: UserEnterpriseInfoViewModel) {
    localStorage.setItem(this.ENTERPRISE_STORAGE_KEY, enterprise.enterpriseId);
    this.selectedEnterprise = enterprise;
    this.selectedEnterpriseSubject.next(this.selectedEnterprise);

    this.updatePermissions();
    this.updatePlantations();
  }

  private _canCreateSubject = new BehaviorSubject<boolean>(false);
  canCreate$ = this._canCreateSubject.asObservable();
  private _canEditSubject = new BehaviorSubject<boolean>(false);
  canEdit$ = this._canEditSubject.asObservable();
  private _canDeleteSubject = new BehaviorSubject<boolean>(false);
  canDelete$ = this._canDeleteSubject.asObservable();
  private _showActionsSubject = new BehaviorSubject<boolean>(false);
  showActions$ = this._showActionsSubject.asObservable();
  updatePermissions() {
    const canCreate = this.isGranted('Activity_Create');
    this._canCreateSubject.next(canCreate);

    const canEdit = this.isGranted('Activity_Update');
    this._canEditSubject.next(canEdit);

    const canDelete = this.isGranted('Activity_Delete');
    this._canDeleteSubject.next(canDelete);

    this._showActionsSubject.next(canEdit || canDelete)

  }

  private _permissionService = inject(PermissionService)
  isGranted(permissionKey: string) {
    const selectedEnterpriseId = this.selectedEnterprise.enterpriseId;
    if (this.selectedEnterprise.isDefaultEnterprise) {
      return this._permissionService.isGrantedOnUserPermissions(permissionKey);
    }
    return this._permissionService.isGrantedByEnterprise(selectedEnterpriseId, permissionKey);
  }

  private _activitiesCountSubject = new BehaviorSubject<number>(0);
  activitiesCount$ = this._activitiesCountSubject.asObservable();
  setActivitiesCount(count: number) {
    this._activitiesCountSubject.next(count)
  }

  landList: ExtendedLandViewModel[] = [];
  plantations: GroupedPlantationViewModel[] = [];
  updatePlantations() {
    this.landList = [];
    this.plantations = [];
    this._plantationService.landList$.pipe(
      filter((res) => res?.length > 0),
      take(1)
    ).subscribe(lands => {
      this.landList = lands.filter(e => e.enterpriseId === this.selectedEnterprise.enterpriseId);

      const plantationMap = new Map<string, GroupedPlantationViewModel>(); // or number, depending on ID type
      for (const land of this.landList) {
         for (const plantation of land.plantations) {
          if (!plantationMap.has(plantation.id)) {
            plantationMap.set(plantation.id, plantation);
          }
        }
      }

      this.plantations = Array.from(plantationMap.values());
    });
  }

  get tempratureUnitOptions() {
    return this.unitsLookup?.find(e => e.id === '5c62e657-31ea-431b-9cd3-4281cc17740a')?.units ?? []
  }

  get windSpeedUnitOptions() {
    return this.unitsLookup.find(e => e.id === '11a701c8-a02e-47c1-83a7-2de534209396')?.units ?? []
  }

  get rainfallUnitOptions() {
    return this.unitsLookup.find(e => e.id === 'ec4c5b5d-86fe-477a-893a-58f47fda758c')?.units ?? []
  }

  get plannedStatus(): ActivityStatusViewModel {
    return this.activityStatuses.find(e => e.name === 'Planned');
  }

  get doneStatus(): ActivityStatusViewModel {
    return this.activityStatuses.find(e => e.name === 'Done');
  }

  get overdueStatus(): ActivityStatusViewModel {
    return this.activityStatuses.find(e => e.name === 'Overdue');
  }
}
